import type { IgvTrack } from './IgvMount'

/**
 * Harness config schema for the IGV multi-CRAM / VCF test harness (GEN-2880).
 *
 * The harness lets a developer paste a JSON config that mounts an IGV browser
 * against arbitrary tracks served from S3 signed URLs, so we can validate the
 * end-to-end loading path for GEN-2611 (up to six CRAMs + comparators).
 */
export type HarnessConfig = {
  genome?: string
  locus?: string
  tracks: IgvTrack[]
}

export type HarnessConfigParseResult =
  | { ok: true; config: HarnessConfig; warnings: string[] }
  | { ok: false; error: string }

/** Cap above which we warn (not fail). GEN-2611 designs to 6 tracks; we leave
 * headroom up to 8 to cover a 3-comparator product if it ever ships. */
export const TRACK_SOFT_LIMIT = 6
export const TRACK_HARD_LIMIT = 16

const ALIGNMENT_FORMATS = new Set(['bam', 'cram'])
const VARIANT_FORMATS = new Set(['vcf'])

function validateTrack(raw: unknown, idx: number): IgvTrack | string {
  if (!raw || typeof raw !== 'object') return `tracks[${idx}] must be an object`
  const t = raw as Record<string, unknown>
  const name = typeof t.name === 'string' ? t.name.trim() : ''
  const format = typeof t.format === 'string' ? t.format.trim().toLowerCase() : ''
  const url = typeof t.url === 'string' ? t.url.trim() : ''
  const indexURL = typeof t.indexURL === 'string' ? t.indexURL.trim() : undefined
  const type = typeof t.type === 'string' ? t.type.trim() : undefined

  if (!name) return `tracks[${idx}].name is required`
  if (!format) return `tracks[${idx}].format is required (e.g. "cram", "bam", "vcf")`
  if (!url) return `tracks[${idx}].url is required`

  if ((ALIGNMENT_FORMATS.has(format) || VARIANT_FORMATS.has(format)) && !indexURL) {
    return `tracks[${idx}].indexURL is required for format "${format}"`
  }

  const inferredType =
    type ?? (ALIGNMENT_FORMATS.has(format) ? 'alignment' : VARIANT_FORMATS.has(format) ? 'variant' : undefined)

  const track: IgvTrack = { name, format, url }
  if (indexURL) track.indexURL = indexURL
  if (inferredType) track.type = inferredType
  return track
}

export function parseHarnessConfig(raw: string): HarnessConfigParseResult {
  const text = raw.trim()
  if (!text) return { ok: false, error: 'Config is empty' }

  let json: unknown
  try {
    json = JSON.parse(text)
  } catch (e) {
    return { ok: false, error: `Invalid JSON: ${e instanceof Error ? e.message : String(e)}` }
  }

  if (!json || typeof json !== 'object') {
    return { ok: false, error: 'Config must be a JSON object' }
  }
  const obj = json as Record<string, unknown>
  const tracksRaw = obj.tracks
  if (!Array.isArray(tracksRaw)) {
    return { ok: false, error: '`tracks` must be an array' }
  }
  if (tracksRaw.length === 0) {
    return { ok: false, error: '`tracks` must contain at least one track' }
  }
  if (tracksRaw.length > TRACK_HARD_LIMIT) {
    return { ok: false, error: `\`tracks\` may not exceed ${TRACK_HARD_LIMIT} entries` }
  }

  const tracks: IgvTrack[] = []
  for (let i = 0; i < tracksRaw.length; i++) {
    const result = validateTrack(tracksRaw[i], i)
    if (typeof result === 'string') return { ok: false, error: result }
    tracks.push(result)
  }

  const warnings: string[] = []
  if (tracks.length > TRACK_SOFT_LIMIT) {
    warnings.push(`Loaded ${tracks.length} tracks; GEN-2611 designs to ${TRACK_SOFT_LIMIT}. Performance may degrade.`)
  }

  const genome = typeof obj.genome === 'string' && obj.genome.trim() ? obj.genome.trim() : undefined
  const locus = typeof obj.locus === 'string' && obj.locus.trim() ? obj.locus.trim() : undefined

  return { ok: true, config: { genome, locus, tracks }, warnings }
}

/** Reverse of {@link parseHarnessConfig}: serialize a config back to canonical
 * JSON. Used for the "copy shareable link" affordance. */
export function stringifyHarnessConfig(config: HarnessConfig): string {
  return JSON.stringify(config, null, 2)
}

const HASH_KEY = 'config'

/** Encode a harness config into a URL hash fragment suitable for sharing /
 * bookmarking a paste-state. Base64 (URL-safe) is used so signed S3 URLs and
 * any `&` / `=` they carry survive the round-trip cleanly. */
export function encodeHarnessConfigToHash(config: HarnessConfig): string {
  const json = JSON.stringify(config)
  const b64 = base64UrlEncode(json)
  return `#${HASH_KEY}=${b64}`
}

export function decodeHarnessConfigFromHash(hash: string | null | undefined): HarnessConfigParseResult | null {
  if (!hash) return null
  const trimmed = hash.startsWith('#') ? hash.slice(1) : hash
  if (!trimmed) return null
  const params = new URLSearchParams(trimmed)
  const value = params.get(HASH_KEY)
  if (!value) return null
  let json: string
  try {
    json = base64UrlDecode(value)
  } catch (e) {
    return { ok: false, error: `Invalid base64 in hash: ${e instanceof Error ? e.message : String(e)}` }
  }
  return parseHarnessConfig(json)
}

function base64UrlEncode(input: string): string {
  const bytes = new TextEncoder().encode(input)
  let binary = ''
  for (const b of bytes) binary += String.fromCharCode(b)
  const b64 = typeof btoa === 'function' ? btoa(binary) : Buffer.from(binary, 'binary').toString('base64')
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function base64UrlDecode(input: string): string {
  const padded = input
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(input.length + ((4 - (input.length % 4)) % 4), '=')
  const binary = typeof atob === 'function' ? atob(padded) : Buffer.from(padded, 'base64').toString('binary')
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new TextDecoder().decode(bytes)
}

export const HARNESS_CONFIG_EXAMPLE: HarnessConfig = {
  genome: 'hg38',
  locus: 'chr1:155234000-155235000',
  tracks: [
    {
      name: 'proband-illumina',
      format: 'cram',
      type: 'alignment',
      url: 'https://your-bucket.s3.amazonaws.com/proband.illumina.cram?<signed>',
      indexURL: 'https://your-bucket.s3.amazonaws.com/proband.illumina.cram.crai?<signed>',
    },
    {
      name: 'proband-ont',
      format: 'cram',
      type: 'alignment',
      url: 'https://your-bucket.s3.amazonaws.com/proband.ont.cram?<signed>',
      indexURL: 'https://your-bucket.s3.amazonaws.com/proband.ont.cram.crai?<signed>',
    },
  ],
}
